import { useEffect } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useEditTourPlan } from '../hooks/useEditTourPlan'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { HiOutlineCheckCircle, HiOutlineTrash } from 'react-icons/hi'
import { useSelector } from 'react-redux'

import { Box, Divider, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  Button,
  DrawerScrollContainer,
  FormInputControl,
} from '@/components/ui'
import { SelectCheckpointFlatListItem } from '@/features/guard-checkpoint-flat-list'
import { UpdateTourPlanInput } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { CheckpointIProps } from '../../types/types'
import { areCheckpointsUpdated } from '../utils/utils'
import { CreateTourPlanHeader } from './CreateTourPlanHeader'
import { DeleteTourPlanModal } from './DeleteTourPlanModal'

interface EditTourPlanIProps {
  isLoading: boolean
  id: string
  name: string
  checkpoints: CheckpointIProps[]
  facilityId: string
  currentFloorId: string
  onClose: () => void
  onUpdateTourPlan: (id: string) => void
  onDeleteTourPlan: () => void
}

export const EditTourPlan = ({
  isLoading,
  id,
  name,
  checkpoints,
  facilityId,
  currentFloorId,
  onClose,
  onUpdateTourPlan,
  onDeleteTourPlan,
}: EditTourPlanIProps) => {
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure()
  const { handleSubmit, register, errors, formState, setValue } = useForm({
    mode: 'onChange',
  })
  const me = useSelector(selectMe)
  const { shouldEnableDeleteTourPlan } = usePermissions()
  const { isValid, isDirty } = formState
  const { setHighlightedDeviceIds } = useFloorPlan()
  const { showSuccess, showError } = useToasts()
  const {
    floorData,
    checkedIds,
    orginalCheckedIds,
    isUpdateLoading,
    handleChecked,
    update,
  } = useEditTourPlan(facilityId, checkpoints)

  useEffect(() => {
    setValue('name', name, { shouldValidate: true })
  }, [name])

  const onSubmit = async (values) => {
    const input: UpdateTourPlanInput = {
      id,
      name: values.name,
      checkpointIds: checkedIds,
    }
    mixpanel.track('Button Press: Tour Plan Details - Edit Tour Save', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    try {
      const data = await update(input)
      const updateTourPlanId = data?.updateTourPlan?.tourPlan?.id
      showSuccess('Updated TourPlan.')
      onUpdateTourPlan(updateTourPlanId)
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleOnCancel = () => {
    mixpanel.track('Button Press: Tour Plan Details - Edit Tour Cancel', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    handleOnClose()
  }

  const handleOnClose = () => {
    setHighlightedDeviceIds(null)
    onClose()
  }

  const handleDeleteModalOpen = () => {
    mixpanel.track('Button Press: Tour Plan Details - Delete Tour', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    onOpen()
  }

  return (
    <>
      <CreateTourPlanHeader edit onClose={handleOnClose} />

      <Box mb={3} padding='16px'>
        <FormInputControl
          data-testid='tourplan_editTourPlan_name'
          errorMessage='You must specify a name'
          id='name'
          inputRef={register({
            required: 'You must specify an name',
            pattern: /\S/,
          })}
          isInvalid={errors.name}
          label='Tour Plan Name'
          placeholder='Enter Tour Plan Name'
          type='name'
        />
      </Box>
      <DrawerScrollContainer padding='16px'>
        <AnimatePresence>
          {floorData?.floors?.edges?.length > 0 ? (
            <Box
              animate={{ opacity: 1 }}
              as={motion.div}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              w='full'
            >
              {floorData?.floors?.edges?.map((f) => (
                <VStack align='stretch' key={f.node?.id}>
                  <Box
                    color='#172B4D'
                    fontSize='18px'
                    fontWeight='medium'
                    paddingY='10px'
                  >
                    {f?.node?.id === currentFloorId
                      ? 'Current Floor'
                      : f?.node?.name}
                  </Box>
                  {f?.node?.devices?.edges.map((d) => (
                    <SelectCheckpointFlatListItem
                      key={d.node?.id}
                      label={d.node?.name}
                      onChecked={() => handleChecked(d.node?.id)}
                      selected={checkedIds?.includes(d.node?.id)}
                      subLabel={d.node?.checkpoint?.displayId}
                    />
                  ))}
                </VStack>
              ))}
            </Box>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              List is empty
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
      <Divider
        bgColor='#D7D4D4'
        display='flex'
        h='2px'
        justifySelf='flex-end'
        mb='10px'
      />

      <Box
        display='flex'
        flexDir='row'
        justifyContent='space-between'
        mb='40px'
        padding='16px'
      >
        <Button fontSize='15px' mr='3' onClick={handleOnCancel} variant='ghost'>
          Cancel
        </Button>

        <Button
          fontSize='15px'
          iconSpacing='5px'
          isDisabled={!shouldEnableDeleteTourPlan}
          leftIcon={<HiOutlineTrash size='24px' />}
          loadingText='Deleting'
          mr='3'
          onClick={handleDeleteModalOpen}
          variant='danger'
        >
          Delete
        </Button>
        <Button
          fontSize='15px'
          iconSpacing='5px'
          isDisabled={
            !isValid ||
            !checkedIds?.length ||
            (!isDirty && !areCheckpointsUpdated(orginalCheckedIds, checkedIds))
          }
          isLoading={isLoading || isUpdateLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Saving'
          onClick={handleSubmit(onSubmit)}
        >
          Save Changes
        </Button>
      </Box>

      <DeleteTourPlanModal
        facilityId={facilityId}
        isOpen={isOpen}
        name={name}
        onClose={onModalClose}
        onDelete={onDeleteTourPlan}
        tourPlanId={id}
      />
    </>
  )
}
